import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { DataTableSelectedData } from '@repo/ui/data-table'
import { DownloadFile, PdfIcon } from '@repo/ui/icons'
import InnerBox from '@repo/ui/inner-box'
import MultiSelectInput from '@repo/ui/multiselect-input'
import { Skeleton } from '@repo/ui/skeleton'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { Fragment, useState } from 'react'

import { API, constructApiUrl } from '../../../api/apis.config'
import { getTripFolderAffectedStaffs } from '../../../api/trip-folder/get-trip-folder-affected-staffs'
import { getTripFolderBookings } from '../../../api/trip-folder/get-trip-folder-bookings'
import { getTripFolderCenters } from '../../../api/trip-folder/get-trip-folder-centers'
import { getTripFolderDeparturePlaces } from '../../../api/trip-folder/get-trip-folder-departure-places'
import { getTripFolderEnrollments } from '../../../api/trip-folder/get-trip-folder-enrollments'
import TripFolderEnrollmentsList from '../../../components/pages/trip-folders/trip-folder-enrollments-list'
import PassengersTripBookingFilterBadge from '../../../components/pages/trip-folders/trip-folder-passengers-list/passengers-trip-booking-filter-badge'
import TripFolderStaffPassengersList from '../../../components/pages/trip-folders/trip-folder-staff-passengers-list'
import { useObserver } from '../../../lib/contexts/observer'
import { useDownloadDocuments } from '../../../lib/hooks/use-download-documents'
import {
  TripFolderEnrollment,
  TripFolderPassengersFilters
} from '../../../lib/types/models/trip-folder/trip-folder-passengers'
import { getTranslatedName } from '../../../lib/utils/functions/translation.functions'
import { queryClient } from '../../../main'
import { checkIsSplittingDisabled, flattenTripFolderBookings } from './utils'

type TripFolderPassengerDetailsProps = {
  folderId: number
}

const defaultFilters: TripFolderPassengersFilters = {
  tripBookingIds: [],
  centerIds: [],
  departurePlaceIds: []
}

const defaultSelectedItems = {
  isAllSelected: false,
  rows: []
}

const TripFolderPassengerDetails = ({ folderId }: TripFolderPassengerDetailsProps) => {
  const { downloadDocuments } = useDownloadDocuments()
  const [selectedFilters, setSelectedFilters] =
    useState<TripFolderPassengersFilters>(defaultFilters)

  const [selectedEnrollments, setSelectedEnrollments] =
    useState<DataTableSelectedData<TripFolderEnrollment>>(defaultSelectedItems)

  const { data: tripFolderBookings, isPending: isTripFolderBookingsPending } = useQuery({
    queryKey: ['trip-folder-bookings', folderId],
    queryFn: () => getTripFolderBookings(folderId)
  })

  const passengerTripBookings = flattenTripFolderBookings(tripFolderBookings ?? [])

  const { data: centers, isPending: isCentersPending } = useQuery({
    queryKey: ['centers', folderId],
    queryFn: () => getTripFolderCenters(folderId)
  })

  const { data: departurePlaces } = useQuery({
    queryKey: ['trip-folder-departure-places', folderId],
    queryFn: () => getTripFolderDeparturePlaces(folderId)
  })

  const { data: tripFolderEnrollments, isPending: isTripFolderEnrollmentsPending } = useQuery({
    queryKey: ['trip-folder-enrollments', folderId, selectedFilters],
    queryFn: () => getTripFolderEnrollments(folderId, { filter: selectedFilters }),
    select: (data) => {
      return data.map((row) => {
        if (row.supervisor) {
          return {
            ...row,
            supervisorName: toFullName(row.supervisor.firstName, row.supervisor.lastName),
            rowColor: tailwindColors.secondary.light.DEFAULT
          }
        }
        return row
      })
    }
  })

  const { data: tripFolderAffectedStaffs, isPending: isTripFolderAffectedStaffsPending } =
    useQuery({
      queryKey: ['trip-folder-affected-staffs', folderId, selectedFilters],
      queryFn: () => getTripFolderAffectedStaffs(folderId, { filter: selectedFilters })
    })

  const disableSplitting = checkIsSplittingDisabled(selectedFilters)

  useObserver({
    key: 'trip-folder-young-passengers',
    update: () => {
      queryClient.invalidateQueries({
        queryKey: ['trip-folder-enrollments', folderId, selectedFilters]
      })
    }
  })

  const handleTripBookingFilter = (tripBookingId: number) => {
    setSelectedFilters((prev) => ({
      ...prev,
      tripBookingIds: prev.tripBookingIds?.includes(tripBookingId)
        ? prev.tripBookingIds?.filter((id) => id !== tripBookingId)
        : [...(prev.tripBookingIds ?? []), tripBookingId]
    }))
  }

  const handleCenterFilter = (centerId: number) => {
    setSelectedFilters((prev) => ({
      ...prev,
      centerIds: prev.centerIds?.includes(centerId)
        ? prev.centerIds?.filter((id) => id !== centerId)
        : [...(prev.centerIds ?? []), centerId]
    }))
  }

  const getEnrollmentsListDownloadFilters = () => ({
    reservations:
      selectedFilters.tripBookingIds?.map((id) => {
        const booking = passengerTripBookings.find((booking) => booking.id === id)
        if (booking?.isTakeInCharge) return `Résa ${id}`
        return `Résa ${booking?.reservationNumber || id}`
      }) ?? [],
    centers:
      selectedFilters.centerIds?.map((id) => {
        const center = centers?.find((center) => center.id === id)
        return center?.name || `Center ${id}`
      }) ?? [],
    tripBookingIds: selectedFilters.tripBookingIds,
    centerIds: selectedFilters.centerIds,
    departurePlaceIds: selectedFilters.departurePlaceIds
  })

  return (
    <div className="py-3 flex flex-col gap-4">
      <Typography variant="sub-heading">Liste des pax</Typography>

      <div className="flex items-center justify-end w-full gap-3">
        <Button
          variant="ghost"
          label="Imprimer les listes par réservation"
          icon={DownloadFile}
          onClick={async () => {
            await downloadDocuments({
              endpoint: constructApiUrl(
                API.tripFolder.downloadTripFolderEnrollmentsListByReservationPdf.url,
                { id: folderId }
              ),
              data: {
                filters: getEnrollmentsListDownloadFilters()
              }
            })
          }}
        />
        <Button
          variant="ghost"
          label="Imprimer les listes par centre"
          icon={DownloadFile}
          onClick={async () => {
            await downloadDocuments({
              endpoint: constructApiUrl(
                API.tripFolder.downloadTripFolderEnrollmentsListByCenterPdf.url,
                { id: folderId }
              ),
              data: {
                filters: getEnrollmentsListDownloadFilters()
              }
            })
          }}
        />
      </div>

      <InnerBox title="Filtres">
        {isTripFolderBookingsPending || isCentersPending ? (
          <div className="flex items-center justify-center h-full">
            <Skeleton className="w-full h-[250px]" />
          </div>
        ) : (
          <Fragment>
            {JSON.stringify(selectedFilters) !== JSON.stringify(defaultFilters) && (
              <Button
                variant="destructive"
                label="Réinitialiser"
                className="w-fit self-end -mt-8"
                onClick={() => setSelectedFilters(defaultFilters)}
              />
            )}

            <div className="flex flex-col mt-4 gap-2">
              <Typography variant="label-semibold">Réservation</Typography>

              <div className="flex flex-wrap items-center gap-2">
                {passengerTripBookings.length ? (
                  passengerTripBookings.map((tripFolderBooking) => (
                    <PassengersTripBookingFilterBadge
                      key={tripFolderBooking.id}
                      tripBooking={tripFolderBooking}
                      active={selectedFilters?.tripBookingIds?.includes(tripFolderBooking.id)}
                      onClick={() => handleTripBookingFilter(tripFolderBooking.id)}
                    />
                  ))
                ) : (
                  <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                    Aucune réservation trouvée
                  </Typography>
                )}
              </div>
            </div>

            <div className="flex flex-col mt-4 gap-2">
              <Typography variant="label-semibold">Centres</Typography>

              <div className="flex flex-wrap items-center gap-2">
                {centers && centers.length > 0 ? (
                  centers.map((center) => (
                    <Badge
                      key={center.id}
                      text={center.name}
                      className="cursor-pointer select-none"
                      variant={
                        selectedFilters?.centerIds?.includes(center.id) ? 'warning' : 'white'
                      }
                      onClick={() => handleCenterFilter(center.id)}
                    />
                  ))
                ) : (
                  <Typography variant="label-medium" color={tailwindColors.grey.light.active}>
                    Aucun centre trouvé
                  </Typography>
                )}
              </div>
            </div>

            <div className="flex flex-col mt-4 gap-2">
              <MultiSelectInput
                searchable
                variant={'primary'}
                label={'Ville de départ'}
                options={
                  departurePlaces?.map((place) => ({
                    label: getTranslatedName(place),
                    value: place.id
                  })) ?? []
                }
                selectedOptions={
                  selectedFilters?.departurePlaceIds?.map((id) => ({
                    label: getTranslatedName(departurePlaces?.find((place) => place.id === id)),
                    value: id
                  })) ?? []
                }
                onChange={(value) =>
                  setSelectedFilters((prev) => ({ ...prev, departurePlaceIds: value }))
                }
              />
            </div>
          </Fragment>
        )}
      </InnerBox>

      <InnerBox>
        <div className={'flex justify-end'}>
          <Button
            variant={'ghost'}
            label={'Imprimer cette liste'}
            icon={PdfIcon}
            disabled={!tripFolderEnrollments?.length}
            onClick={async () => {
              await downloadDocuments({
                endpoint: constructApiUrl(API.tripFolder.printRollCall.url, { id: folderId }),
                data: {
                  enrollmentIds: selectedEnrollments.rows.map((row) => row.id),
                  filters: getEnrollmentsListDownloadFilters()
                }
              })
            }}
          />
        </div>

        <TripFolderStaffPassengersList
          data={tripFolderAffectedStaffs ?? []}
          isLoading={isTripFolderAffectedStaffsPending}
        />

        <div className={'h-4'} />

        <TripFolderEnrollmentsList
          data={tripFolderEnrollments ?? []}
          isLoading={isTripFolderEnrollmentsPending}
          folderId={folderId}
          disableSplitting={disableSplitting}
          selectedFilters={selectedFilters}
          selectable
          selectedData={selectedEnrollments}
          onSelectedDataChange={setSelectedEnrollments}
        />
      </InnerBox>
    </div>
  )
}

export default TripFolderPassengerDetails
